import { useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  Container,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import { Link as RouterLink, NavLink, Outlet, useNavigate } from "react-router-dom";
import DccLogo from "./DccLogo";
import BotonTema from "../BotonTema";
import { tokens } from "../../theme";

const NAV = [
  { label: "Temas", to: "/temas" },
  { label: "Preguntas Frecuentes", to: "/preguntas" },
];

// Ancho máximo compartido por header y footer para que su contenido quede alineado.
const MAX_WIDTH = "lg" as const;

/**
 * Layout del sitio público (Design Kit DCC): header azul con marca + navegación
 * y footer con marca de agua. El contenido de cada página entra por <Outlet/>.
 */
export default function PublicLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const navLinkStyle = ({ isActive }: { isActive: boolean }) => ({
    color: tokens.neutro.blanco,
    textDecoration: "none",
    fontSize: 14,
    opacity: isActive ? 1 : 0.85,
    borderBottom: `2px solid ${isActive ? tokens.naranjo[400] : "transparent"}`,
    paddingBottom: 2,
  });

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
      <AppBar position="static" sx={{ bgcolor: "primary.main" }}>
        <Container maxWidth={MAX_WIDTH}>
          <Toolbar disableGutters sx={{ py: 1, gap: 2 }}>
            {/* Marca */}
            <Box
              component={RouterLink}
              to="/"
              sx={{ display: "flex", alignItems: "center", gap: 1.5, textDecoration: "none", flexGrow: 1 }}
            >
              <DccLogo height={32} />
              <Typography
                sx={{ fontFamily: tokens.fuente.titulos, fontWeight: 600, fontSize: 16, color: tokens.neutro.blanco, letterSpacing: "0.16em" }}
              >
                TITULACIÓN
              </Typography>
            </Box>

            <BotonTema color={tokens.neutro.blanco} />

            {!isMobile ? (
              <Stack direction="row" alignItems="center" spacing={3.5}>
                {NAV.map((n) => (
                  <NavLink key={n.to} to={n.to} style={navLinkStyle}>
                    {n.label}
                  </NavLink>
                ))}
                {/* Siempre al login, aunque el portal esté configurado: ahí están las
                    dos puertas. Mandar directo a U-Pasaporte dejaba afuera al profesor
                    externo, que no tiene cómo autenticarse ahí y no tenía desde dónde
                    llegar al formulario local. */}
                <Button
                  variant="contained"
                  onClick={() => navigate("/login")}
                  sx={{ bgcolor: tokens.neutro.blanco, color: "primary.main", "&:hover": { bgcolor: tokens.azul[50] } }}
                >
                  Entrar
                </Button>
              </Stack>
            ) : (
              <IconButton onClick={() => setOpen(true)} sx={{ color: tokens.neutro.blanco }} aria-label="Abrir menú">
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Menú móvil */}
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 260 }} role="presentation" onClick={() => setOpen(false)}>
          <List>
            {NAV.map((n) => (
              <ListItemButton key={n.to} component={RouterLink} to={n.to}>
                <ListItemText primary={n.label} />
              </ListItemButton>
            ))}
          </List>

          {/* "Entrar" es la acción del menú, no un destino más de la lista: va como
              botón, igual que en la barra. Ahí es blanco porque se apoya en el azul de
              la AppBar; acá el fondo es el del panel, así que se invierte —azul con
              texto blanco— y así se lee en claro y en oscuro. */}
          <Divider />
          <Box sx={{ p: 2 }}>
            <Button fullWidth variant="contained" component={RouterLink} to="/login">
              Entrar
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Contenido de cada página pública. Es columna flexible para que una página
          que quiera centrarse en el alto disponible —el login— pueda estirarse con
          `flexGrow`; las demás se comportan igual que antes. */}
      <Box component="main" sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Outlet />
      </Box>

      <PublicFooter />
    </Box>
  );
}

function PublicFooter() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box component="footer" sx={{ position: "relative", overflow: "hidden", bgcolor: tokens.pie.fondo, py: { xs: 4.5, md: 6.5 } }}>
      {/* Marca de agua */}
      <Box
        aria-hidden
        sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}
      >
        <Box
          component="span"
          sx={{
            fontFamily: tokens.fuente.titulos,
            fontWeight: 800,
            fontSize: "clamp(80px, 22vw, 180px)",
            color: tokens.pie.textoFuerte,
            opacity: 0.07,
            letterSpacing: "0.06em",
            whiteSpace: "nowrap",
          }}
        >
          TITULACIÓN
        </Box>
      </Box>

      <Container maxWidth={MAX_WIDTH} sx={{ position: "relative" }}>
        {/* Siempre en fila: logo a la izquierda, datos a la derecha (también en móvil). */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={{ xs: 1, sm: 2.5 }}>
          <DccLogo height={isMobile ? 36 : 54} />
          <Box sx={{ textAlign: "right", fontSize: { xs: 12, sm: 13 }, lineHeight: 1.6, color: tokens.pie.texto }}>
            Beauchef 851, Edificio Norte
            <br />
            <Box component="b" sx={{ color: tokens.pie.textoFuerte }}>
              Departamento de Ciencias de la Computación
            </Box>
            <br />
            Universidad de Chile
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
